import React, { FC } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { COLORS, InputFieldWrapper, TYPOGRAPHY_COMMON_STYLES } from "components";
import { useNotify } from "hooks";
import { APPLICATION_JSON, QUERY_KEYS } from "consts";
import { checkNullValue, getDateFormatset } from "utils";
import { addEmissionFactorDataByCollector, editEmissionFactorByCollector } from "api/rec";
import { MRT_ColumnDef } from "material-react-table";

interface FormValues {
  providerName: string;
  utilityTypeName: string;
  renewableEnergy: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  efValue: string;
  efUnit: string;
}

interface CollectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  columns: MRT_ColumnDef<any>[];
  editEmissionFactor?: any;
  collectorData: any;
}

export const UtilitySpecificEmissionsFactorsModal: FC<CollectorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  columns,
  editEmissionFactor,
  collectorData,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { collectorId } = useParams();

  const defaultValues: FormValues = editEmissionFactor
    ? {
      providerName: editEmissionFactor.providerName,
      utilityTypeName: editEmissionFactor.utilityType,
      renewableEnergy: editEmissionFactor.renewableEnergy,
      startDate: dayjs(editEmissionFactor.startDate.substring(0, 10)),
      endDate: dayjs(editEmissionFactor.endDate.substring(0, 10)),
      efValue: editEmissionFactor.efValue,
      efUnit: editEmissionFactor.efUnit,
    }
    : {
      providerName: collectorData?.providerName || "",
      utilityTypeName: collectorData?.utilityTypeName || "",
      renewableEnergy: "",
      startDate: null,
      endDate: null,
      efValue: "",
      efUnit: "",
    };

  const helperTextWrapper = (errorMessage: string) =>
    <FormHelperText
      sx={{
        color: "red",
        fontFamily: "Manrope",
        fontSize: "11px",
        whiteSpace: "pre-wrap",
        overflow: "hidden",
        width: "240px",

        "&.MuiFormHelperText-root": {
          margin: "0px",
          marginLeft: "-11px",
          color: "red",
        },
      }}
    >
      {errorMessage}
    </FormHelperText>

  const { control, handleSubmit, formState: { isValid } } = useForm<FormValues>({
    mode: "onChange",
    defaultValues,
    resolver: async (data) => {
      const errors: any = {};
      // Required validations for all fields except renewableEnergy
      if (!data.providerName) {
        errors.providerName = { message: "Input is required." };
      }
      if (!data.utilityTypeName) {
        errors.utilityTypeName = { message: "Input is required." };
      }
      if (!data.startDate) {
        errors.startDate = { message: "Input is required." };
      }
      if (!data.endDate) {
        errors.endDate = { message: "Input is required." };
      }
      if (!data.efValue) {
        errors.efValue = { message: "Input is required." };
      } else if (Number.isNaN(Number(data.efValue))) {
        errors.efValue = { message: "Input must be a numerical value." };
      }
      if (!data.efUnit) {
        errors.efUnit = { message: "Input is required." };
      }

      // Validate renewableEnergy only if provided
      if (data.renewableEnergy) {
        const renewableValue = Number(data.renewableEnergy);
        if (Number.isNaN(renewableValue)) {
          errors.renewableEnergy = { message: "Input must be a numerical value." };
        } else if (renewableValue < 0 || renewableValue > 100) {
          errors.renewableEnergy = { message: "Please enter a percentage between 0 and 100." };
        }
      }
      return {
        values: Object.keys(errors).length ? {} : data,
        errors,
      };
    },
  });

  const { mutateAsync: addEmissionFactorDocument } = useMutation(addEmissionFactorDataByCollector);
  const { mutateAsync: editEmissionFactorDocument } = useMutation(editEmissionFactorByCollector);

  const emissionFactorUnits = ["kg/kWh", "g/kWh", "lb/MWh"] as const;

  const createFormData = (data: FormValues) => {
    const payload = {
      collectorId,
      utilityProvider: data.providerName,
      utilityTypeName: data.utilityTypeName,
      efValue: data.efValue,
      efUnit: data.efUnit,
      renewableEnergy: data.renewableEnergy,
      startDate: checkNullValue(data.startDate?.format("YYYY-MM-DDT00:00:00[Z]")),
      endDate: checkNullValue(data.endDate?.format("YYYY-MM-DDT00:00:00[Z]")),
    };

    const formData = new FormData();
    formData.append("data", new Blob([JSON.stringify(payload)], { type: APPLICATION_JSON }));
    return formData;
  };

  const onSubmit = async (data: FormValues) => {
    const formData = createFormData(data);
    const mutation = editEmissionFactor
      ? editEmissionFactorDocument({ collectorId, payload: formData, id: editEmissionFactor.id })
      : addEmissionFactorDocument({ collectorId, payload: formData });

    try {
      await mutation;
      notify.success(`Emission Factor have been successfully ${editEmissionFactor ? "edited" : "added"}!`);
      queryClient.invalidateQueries([QUERY_KEYS.SITES]);
      onSave();
    } catch (error: any) {
      notify.error(error?.response?.data || `Error ${editEmissionFactor ? "editing" : "adding"} Emission Factor!`);
    }
  };

  const renderField = (column: any) => (
    <Grid item xs={8} sm={6} key={column.header}>
      <Controller
        name={column.accessorKey === 'utilityType' ? 'utilityTypeName' as keyof FormValues : column.accessorKey as keyof FormValues}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputFieldWrapper label={column.accessorKey === "renewableEnergy" ? column.header : `${column.header} *`}>
            {["providerName", "utilityType"].includes(column.accessorKey) ? (
              <TextField
                {...field}
                size="small"
                disabled
                fullWidth
                variant="outlined"
                error={!!error}
                helperText={helperTextWrapper(error?.message || '')}
              />
            ) : column.accessorKey === "efUnit" ? (
              <Select
                {...field}
                displayEmpty
                sx={{ width: "100%", height: "36px" }}
                value={field.value || ""}
                error={!!error}
              >
                {emissionFactorUnits.map((unit) => (
                  <MenuItem
                    key={unit}
                    value={unit}
                    sx={{
                      fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                      fontSize: 14,
                      lineHeight: "18px",
                      color: "#90959E",
                      "&:hover": { backgroundColor: "#F6F8FA", borderRadius: 5, color: COLORS.Romance },
                      "&.Mui-selected": { backgroundColor: "#F6F8FA", borderRadius: 5, color: COLORS.Romance },
                    }}
                  >
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            ) : ["startDate", "endDate"].includes(column.accessorKey) ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  {...field}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: "100%" },
                      error: !!error,
                      helperText: helperTextWrapper(error?.message || '')
                    },
                  }}
                  format={getDateFormatset()}
                />
              </LocalizationProvider>
            ) : (
              <TextField
                {...field}
                size="small"
                error={!!error}
                helperText={helperTextWrapper(error?.message || '')}
                InputProps={
                  column.accessorKey === "renewableEnergy"
                    ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <span style={{ color: COLORS.Romance }}>%</span>
                        </InputAdornment>
                      ),
                    }
                    : undefined
                }
                fullWidth
                variant="outlined"
              />
            )}
          </InputFieldWrapper>

        )}
      />
    </Grid>
  );

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {editEmissionFactor ? "Edit" : "New"} Utility Specific Emissions Factors Details
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={3}>
            {columns.map(renderField)}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container alignItems="center" justifyContent="flex-end" mb={2} ml={2}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isValid}
            >
              {editEmissionFactor ? "Save" : "Add"}
            </Button>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};